import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const expireSession = () => setUser(null);
    window.addEventListener("raava:session-expired", expireSession);
    return () => window.removeEventListener("raava:session-expired", expireSession);
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("raava_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => sessionStorage.removeItem("raava_token"))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (identifier, password = "", method = null) => {
    const r = await api.post("/auth/login", {
      identifier,
      password: password || null,
      login_method: method === "key" || method === "email" ? method : null,
    });
    sessionStorage.setItem("raava_token", r.data.access_token);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const refreshUser = useCallback(async () => {
    if (!sessionStorage.getItem("raava_token")) return null;
    const response = await api.get("/auth/me");
    setUser(response.data);
    return response.data;
  }, []);

  const register = useCallback(async (name, email, password, termsVersion) => {
    const r = await api.post("/auth/register", {
      name,
      email,
      password,
      terms_accepted: true,
      terms_version: termsVersion,
    });
    return r.data;
  }, []);

  const logout = useCallback(() => {
    api.post("/auth/logout").catch(() => {});
    sessionStorage.removeItem("raava_token");
    setUser(null);
  }, []);

  const acceptSession = useCallback((data) => {
    sessionStorage.setItem("raava_token", data.access_token);
    setUser(data.user);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, acceptSession, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
